import {useMemo} from 'react';

import {Messages} from '../../../ChatView/types';
import {useWFHooks} from '../../../wfReactInterface/context';
import {ObjectVersionSchema} from '../../../wfReactInterface/wfDataModelHooksInterface';

export interface PromptVersion {
  label: string;
  value: string; // unique identifier for this version (e.g., "prompt-name:v2")
  messages: Messages;
  versionIndex: number;
  isLatest: boolean;
  digest: string;
}

export interface PromptOption {
  label: string;
  value: string; // objectId of the prompt
  versions: PromptVersion[];
  latestVersion: PromptVersion | null;
}

/**
 * Hook to fetch all prompt versions from the API
 */
export const usePrompts = (entity: string, project: string) => {
  const {useRootObjectVersions} = useWFHooks();

  return useRootObjectVersions({
    entity,
    project,
    filter: {
      baseObjectClasses: ['Prompt'],
      latestOnly: false, // Fetch all versions
    },
    metadataOnly: false,
  });
};

/**
 * Groups prompts by objectId and constructs nested options with versions
 */
export const constructPromptOptions = (
  promptsResult: ObjectVersionSchema[]
): PromptOption[] => {
  if (!promptsResult || promptsResult.length === 0) return [];

  // Filter for MessagesPrompt type
  const messagesPrompts = promptsResult.filter(
    prompt => prompt.val?._type === 'MessagesPrompt' && prompt.val?.messages
  );

  // Group prompts by objectId
  const promptGroups = messagesPrompts.reduce(
    (acc, prompt) => {
      const objectId = prompt.objectId;
      if (!acc[objectId]) {
        acc[objectId] = [];
      }
      acc[objectId].push(prompt);
      return acc;
    },
    {} as Record<string, ObjectVersionSchema[]>
  );

  // Convert groups to options with versions
  return Object.entries(promptGroups).map(([objectId, versions]) => {
    // Sort versions by versionIndex descending (latest first)
    const sortedVersions = [...versions].sort(
      (a, b) => b.versionIndex - a.versionIndex
    );

    const promptVersions: PromptVersion[] = sortedVersions.map(version => {
      const name = version.val?.name || objectId;
      return {
        label: `${name}:v${version.versionIndex}`,
        value: `${objectId}:v${version.versionIndex}`,
        messages: (version.val?.messages as Messages) || [],
        versionIndex: version.versionIndex,
        isLatest: version.versionIndex === sortedVersions[0].versionIndex,
        digest: version.versionHash,
      };
    });

    const latestVersion = promptVersions[0] || null;
    const promptName = sortedVersions[0]?.val?.name || objectId;

    return {
      label: promptName,
      value: objectId,
      versions: promptVersions,
      latestVersion,
    };
  });
};

/**
 * Hook that combines prompt fetching and option construction
 */
export const usePromptOptions = (entity: string, project: string) => {
  const prompts = usePrompts(entity, project);

  const promptOptions = useMemo(() => {
    return constructPromptOptions(prompts.result || []);
  }, [prompts.result]);

  return {
    prompts,
    promptOptions,
    loading: prompts.loading,
    error: prompts.error,
  };
};
